// 통계 탭의 "클럽 트렌드" 섹션 — 모든 회원에게 공개되는 집계.
//
// 입력은 (events, details, members). events는 클럽 전체 일정 리스트(상태별
// 필터링은 내부에서 다시), details는 최근 N개 완료 일정의 풀 detail, members는
// 회원 마스터.
//
// 블록 순서: 월별 일정 수 → 활발한 회원 → 활발한 코트 → 팀전 MVP →
// 개인전 우승 → 베스트 조합(타입별) → 성별/레벨 분포. 데이터가 비면 마지막
// 안내 카피로 fallback.
//
// 베스트 조합은 5게임 이상 함께한 조합만 노출(통계적 노이즈 컷). 무승부만
// 본 조합은 rate=0이라 잘못 노출되는 케이스가 있어 교류전 라인업 도구로
// 쓰기 어려운 점을 카피로 보완.

import { Fragment } from "preact";
import { matchPlayerName } from "../matches";
import { navigate } from "../routes";
import { StatCard } from "../ui/components/StatCard";
import { surfaceLabel } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { pickTeamMvp } from "./index";

interface MemberLike {
  member_id?: string;
  member_status?: string;
  gender?: string | null;
  level_category?: string | null;
}

interface MonthEntry { ym: string; count: number; }
interface ActiveMember { name: string; count: number; events: number; }
interface CourtUse { label: string; surface: string | null; location: string | null; count: number; }
interface ComboRow { mt: string; names: string[]; wins: number; losses: number; draws: number; total: number; decided: number; rate: number; }

const MIN_COMBO_GAMES = 5;
const COMBO_TYPES: Array<{ key: string; label: string }> = [
  { key: "men_doubles", label: "남복 베스트 조합" },
  { key: "women_doubles", label: "여복 베스트 조합" },
  { key: "mixed_doubles", label: "혼복 베스트 조합" },
  { key: "exceptional_mix", label: "잡복 베스트 조합" },
];

function computeMonthEntries(events: any[]): MonthEntry[] {
  const byMonth = new Map<string, number>();
  for (const ev of events) {
    if (ev.status !== "completed") continue;
    const ym = (ev.event_date || "").slice(0, 7);
    if (!ym) continue;
    byMonth.set(ym, (byMonth.get(ym) || 0) + 1);
  }
  return [...byMonth.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-6)
    .map(([ym, count]) => ({ ym, count }));
}

function computeActiveMembers(details: any[]): ActiveMember[] {
  const counts = new Map<string, ActiveMember>();
  for (const d of details) {
    for (const p of (d.participants || [])) {
      if (!p.member_id) continue;
      const matchCount = (d.matches || []).filter((m: any) =>
        m.result_status !== "skipped" && (m.player_ids || []).includes(p.event_participant_id),
      ).length;
      if (!counts.has(p.member_id)) counts.set(p.member_id, { name: matchPlayerName(p), count: 0, events: 0 });
      const rec = counts.get(p.member_id)!;
      rec.count += matchCount;
      rec.events += 1;
    }
  }
  return [...counts.values()]
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count || b.events - a.events)
    .slice(0, 5);
}

function computeCourtUse(details: any[]): CourtUse[] {
  const byKey = new Map<string, CourtUse>();
  for (const d of details) {
    const ev = d.event || {};
    const loc = ev.location || null;
    const surfaceByCourtId = new Map<string, string | null>();
    for (const c of (d.courts || [])) {
      if (c.event_court_id) surfaceByCourtId.set(c.event_court_id, c.surface_type || null);
    }
    for (const m of (d.matches || [])) {
      if (m.result_status === "skipped") continue;
      const label = m.court_label;
      if (!label) continue;
      const surface = surfaceByCourtId.get(m.event_court_id) || null;
      const key = `${loc || ""}|${label}|${surface || ""}`;
      if (!byKey.has(key)) byKey.set(key, { label, surface, location: loc, count: 0 });
      byKey.get(key)!.count += 1;
    }
  }
  return [...byKey.values()].sort((a, b) => b.count - a.count).slice(0, 4);
}

function computeIndivWinner(d: any): any | null {
  const matches = (d.matches || []).filter((m: any) => m.winner_side);
  if (matches.length === 0) return null;
  const pMap = new Map<string, any>();
  for (const p of (d.participants || [])) {
    pMap.set(p.event_participant_id, { name: matchPlayerName(p), wins: 0, losses: 0, draws: 0, scored: 0, conceded: 0 });
  }
  for (const m of matches) {
    const sA = (m.sides || []).find((s: any) => s.side_number === 1);
    const sB = (m.sides || []).find((s: any) => s.side_number === 2);
    if (!sA || !sB) continue;
    const scA = Number(m.side_a_score) || 0;
    const scB = Number(m.side_b_score) || 0;
    const bump = (pids: string[], f: string) => pids.forEach((id) => { if (pMap.has(id)) pMap.get(id)[f]++; });
    const addSc = (pids: string[], s: number, c: number) => pids.forEach((id) => {
      if (pMap.has(id)) { pMap.get(id).scored += s; pMap.get(id).conceded += c; }
    });
    if (m.winner_side === "side_a") { bump(sA.player_ids || [], "wins"); bump(sB.player_ids || [], "losses"); }
    else if (m.winner_side === "side_b") { bump(sB.player_ids || [], "wins"); bump(sA.player_ids || [], "losses"); }
    else { bump(sA.player_ids || [], "draws"); bump(sB.player_ids || [], "draws"); }
    addSc(sA.player_ids || [], scA, scB);
    addSc(sB.player_ids || [], scB, scA);
  }
  const ranked = [...pMap.values()]
    .filter((r) => (r.wins + r.losses + r.draws) > 0)
    .sort((a, b) => {
      const at = a.wins + a.losses + a.draws;
      const bt = b.wins + b.losses + b.draws;
      const ra = at ? a.wins / at : 0;
      const rb = bt ? b.wins / bt : 0;
      if (rb !== ra) return rb - ra;
      if (b.wins !== a.wins) return b.wins - a.wins;
      return (b.scored - b.conceded) - (a.scored - a.conceded);
    });
  return ranked[0] || null;
}

function computeCombos(details: any[]): Map<string, { mt: string; names: string[]; wins: number; losses: number; draws: number }> {
  const combos = new Map<string, { mt: string; names: string[]; wins: number; losses: number; draws: number }>();
  for (const d of details) {
    for (const m of (d.matches || [])) {
      if (!m.winner_side || m.result_status === "skipped") continue;
      const mt = m.match_type;
      if (!mt) continue;
      for (const side of (m.sides || [])) {
        const pids: string[] = (side.player_ids || []);
        if (pids.length !== 2) continue;
        const key = [mt, ...pids.slice().sort()].join("|");
        if (!combos.has(key)) {
          const names = pids.map((pid) => {
            for (const pl of (side.players || [])) {
              if ((pl.event_participant_id || (pl.participant || {}).event_participant_id) === pid) return matchPlayerName(pl);
            }
            return pid;
          });
          combos.set(key, { mt, names, wins: 0, losses: 0, draws: 0 });
        }
        const rec = combos.get(key)!;
        const isSideA = side.side_number === 1;
        const won = (isSideA && m.winner_side === "side_a") || (!isSideA && m.winner_side === "side_b");
        if (m.winner_side === "draw") rec.draws++;
        else if (won) rec.wins++;
        else rec.losses++;
      }
    }
  }
  return combos;
}

function rankCombosByType(combos: ReturnType<typeof computeCombos>, mt: string): ComboRow[] {
  return [...combos.values()]
    .filter((r) => r.mt === mt)
    .map((r) => {
      const total = r.wins + r.losses + r.draws;
      const decided = r.wins + r.losses;
      const rate = decided > 0 ? r.wins / decided : 0;
      return { ...r, total, decided, rate };
    })
    .filter((r) => r.total >= MIN_COMBO_GAMES)
    .sort((a, b) => b.rate - a.rate || b.total - a.total)
    .slice(0, 5);
}

// ---------- Section components ----------

function MonthlySparklineSection({ entries }: { entries: MonthEntry[] }) {
  if (entries.length < 2) return null;
  const max = Math.max(...entries.map((e) => e.count), 1);
  return (
    <Fragment>
      <p class="stat-block__label">월별 일정 수</p>
      <div class="sparkline">
        {entries.map(({ ym, count }) => {
          const pct = Math.round((count / max) * 100);
          return (
            <div key={ym} class="sparkline__cell">
              <div class="sparkline__bar">
                <span
                  class="sparkline__bar-fill"
                  style={`height:${Math.max(4, pct)}%;background:var(--brand)`}
                />
              </div>
              <span class="sparkline__val">{count}</span>
              <span class="sparkline__mon">{Number(ym.slice(5, 7))}월</span>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
}

function ActiveMembersSection({ rows }: { rows: ActiveMember[] }) {
  if (rows.length === 0) return null;
  return (
    <Fragment>
      <p class="stat-block__label">활발한 회원 (최근 5개 일정 기준)</p>
      <div class="player-stat-list">
        {rows.map((r, i) => (
          <div key={i} class="player-stat-row">
            <span class="player-stat-row__rank">{i + 1}</span>
            <span class="player-stat-row__name">{r.name}</span>
            <span class="player-stat-row__meta">{r.events}개 일정 · {r.count}경기</span>
          </div>
        ))}
      </div>
    </Fragment>
  );
}

function ActiveCourtsSection({ courts }: { courts: CourtUse[] }) {
  if (courts.length === 0) return null;
  return (
    <Fragment>
      <p class="stat-block__label">활발한 코트</p>
      <div class="stat-grid">
        {courts.map((c, i) => {
          const surfaceText = c.surface ? surfaceLabel(c.surface) : null;
          const parts = [c.location, c.label, surfaceText].filter(Boolean);
          return <StatCard key={i} label={parts.join(" · ")} value={`${c.count}경기`} />;
        })}
      </div>
    </Fragment>
  );
}

function TeamMvpSection({ items }: { items: { event: any; mvp: any }[] }) {
  if (items.length === 0) return null;
  return (
    <Fragment>
      <p class="stat-block__label">최근 팀전 MVP</p>
      <div class="player-stat-list">
        {items.slice(0, 5).map((item, i) => (
          <div
            key={i}
            class="player-stat-row player-stat-row--clickable"
            onClick={() => navigate({ name: "event", id: item.event.event_id })}
          >
            <span class="player-stat-row__trophy">🏆</span>
            <span class="player-stat-row__name">{item.mvp.name}</span>
            <span class="player-stat-row__meta">{item.event.event_date || ""} · {item.event.title || ""}</span>
          </div>
        ))}
      </div>
    </Fragment>
  );
}

function IndivWinnersSection({ items }: { items: { event: any; winner: any }[] }) {
  if (items.length === 0) return null;
  return (
    <Fragment>
      <p class="stat-block__label">최근 개인전 우승</p>
      <div class="player-stat-list">
        {items.slice(0, 5).map((item, i) => {
          const w = item.winner;
          const total = w.wins + w.losses + w.draws;
          return (
            <div
              key={i}
              class="player-stat-row player-stat-row--clickable"
              onClick={() => navigate({ name: "event", id: item.event.event_id })}
            >
              <span class="player-stat-row__trophy">🥇</span>
              <span class="player-stat-row__name">{w.name}</span>
              <span class="player-stat-row__meta">
                {item.event.event_date || ""} · {w.wins}승 {w.losses}패 ({total}경기)
              </span>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
}

function ComboBlock({ label, rows }: { label: string; rows: ComboRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Fragment>
      <p class="stat-block__label" style="margin-top:16px">{label}</p>
      <div class="player-stat-list">
        {rows.map((r, i) => {
          const pct = r.decided > 0 ? Math.round(r.rate * 100) : 0;
          return (
            <div key={i} class="player-stat-row">
              <span class="player-stat-row__rank">{i + 1}</span>
              <span class="player-stat-row__name">{r.names.join(" · ")}</span>
              <span class="player-stat-row__meta">
                {r.wins}승 {r.losses}패 · 승률 {pct}% ({r.total}게임)
              </span>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
}

function GenderRatioSection({ males, females }: { males: number; females: number }) {
  const total = males + females;
  if (total === 0) return null;
  const items = [
    { label: "남", count: males, color: "#0ea5e9" },
    { label: "여", count: females, color: "#ec4899" },
  ].filter((i) => i.count > 0);
  return (
    <Fragment>
      <p class="stat-block__label" style="margin-top:16px">성별 비율 ({total}명)</p>
      <div>
        <div class="type-bar">
          {items.map((it) => {
            const pct = Math.round((it.count / total) * 100);
            return (
              <span
                key={it.label}
                class="type-bar__seg"
                style={`width:${pct}%;background:${it.color}`}
                title={`${it.label} ${it.count}명 (${pct}%)`}
              />
            );
          })}
        </div>
        <div class="type-bar__legend">
          {items.map((it) => (
            <span key={it.label} class="type-bar__legend-item">
              <span class="type-bar__dot" style={`background:${it.color}`} />
              {it.label} {it.count}
            </span>
          ))}
        </div>
      </div>
    </Fragment>
  );
}

function LevelDistSection({ rows }: { rows: Array<[string, number]> }) {
  if (rows.length === 0) return null;
  const maxCnt = Math.max(...rows.map(([, c]) => c), 1);
  return (
    <Fragment>
      <p class="stat-block__label" style="margin-top:16px">레벨 분포</p>
      <div class="hbar-list">
        {rows.map(([lv, cnt]) => {
          const pct = Math.round((cnt / maxCnt) * 100);
          return (
            <div key={lv} class="hbar-row">
              <span class="hbar-row__label">{lv}</span>
              <div class="hbar-row__track">
                <div class="hbar-row__fill" style={`width:${Math.max(4, pct)}%`} />
              </div>
              <span class="hbar-row__val">{cnt}명</span>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
}

function ClubTrends({
  events,
  details,
  members,
}: {
  events: any[];
  details: any[];
  members: MemberLike[] | null;
}) {
  const monthEntries = computeMonthEntries(events);
  const activeMembers = computeActiveMembers(details);
  const courts = computeCourtUse(details);

  const mvps: { event: any; mvp: any }[] = [];
  for (const d of details) {
    const ev = d.event || {};
    if (ev.mode !== "team" || ev.event_type !== "special") continue;
    const mvp = pickTeamMvp(d);
    if (mvp) mvps.push({ event: ev, mvp });
  }

  const indivWinners: { event: any; winner: any }[] = [];
  for (const d of details) {
    const ev = d.event || {};
    if (ev.mode === "team" || ev.event_type !== "special") continue;
    const winner = computeIndivWinner(d);
    if (winner) indivWinners.push({ event: ev, winner });
  }

  const combos = computeCombos(details);
  const comboBlocks = COMBO_TYPES.map((ct) => ({ ...ct, rows: rankCombosByType(combos, ct.key) }));
  const hasAnyCombo = comboBlocks.some((b) => b.rows.length > 0);
  const hasComboData = combos.size > 0;

  let males = 0, females = 0;
  let levelRows: Array<[string, number]> = [];
  if (members && members.length > 0) {
    const included = members.filter((m) => {
      const st = m.member_status || "active";
      return st === "active" || st === "on_leave";
    });
    males = included.filter((m) => m.gender === "male").length;
    females = included.filter((m) => m.gender === "female").length;
    const levelCounts = new Map<string, number>();
    for (const m of included) {
      const lv = m.level_category || "미설정";
      levelCounts.set(lv, (levelCounts.get(lv) || 0) + 1);
    }
    levelRows = [...levelCounts.entries()].sort((a, b) => b[1] - a[1]);
  }

  const hasMemberDist = males + females > 0 || levelRows.length > 0;
  const hasAnything =
    monthEntries.length >= 2 ||
    activeMembers.length > 0 ||
    courts.length > 0 ||
    mvps.length > 0 ||
    indivWinners.length > 0 ||
    hasAnyCombo ||
    hasMemberDist;

  return (
    <div class="section section--panel">
      <div class="section__title"><h3>클럽 트렌드</h3></div>
      <MonthlySparklineSection entries={monthEntries} />
      <ActiveMembersSection rows={activeMembers} />
      <ActiveCourtsSection courts={courts} />
      <TeamMvpSection items={mvps} />
      <IndivWinnersSection items={indivWinners} />
      {comboBlocks.map((b) => <ComboBlock key={b.key} label={b.label} rows={b.rows} />)}
      {hasAnyCombo && (
        <p class="muted small" style="margin-top:8px">
          교류전 라인업 고민될 때 참고하세요. 데이터가 쌓일수록 정확해져요.
        </p>
      )}
      {!hasAnyCombo && hasComboData && (
        <Fragment>
          <p class="stat-block__label" style="margin-top:16px">베스트 조합</p>
          <p class="muted small">{MIN_COMBO_GAMES}게임 이상 함께 뛴 조합이 생기면 표시돼요.</p>
        </Fragment>
      )}
      <GenderRatioSection males={males} females={females} />
      <LevelDistSection rows={levelRows} />
      {!hasAnything && <p class="muted small">완료된 일정이 쌓이면 여기에 트렌드가 표시돼요.</p>}
    </div>
  );
}

export function buildClubTrendsSection(
  events: any[],
  details: any[],
  members: MemberLike[] | null,
): HTMLElement {
  return mountPreact(<ClubTrends events={events} details={details} members={members} />);
}
